import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  GetInitialBalances,
  GetManualDocuments,
} from "../../../graphql/initialBalanceSidebar";
import { GetBankBalanceDataHolding } from "../../../graphql/holding";
import { formatMoney, formatMoneyWithCurrency } from "../../../utils/formatter";
import moment from "moment";
import AddedBalance from "./addedBalance";
import BankCard from "./bankCard";
import { Button, Icon } from "semantic-ui-react";
import "../../../scss/_semanticForms.scss";

import "./initialBalance.scss";
import { importOpenSidebar } from "src/redux/actions/openSidebar";
import { importUpdateBalanceNeeded, importUpdateCashflowNeeded } from "src/redux/actions/flujocaja";
import { importUpdateHoldingCashFlowNeeded } from "src/redux/actions/holdingCashFlow";

export default function InitialBalance() {
  const { t } = useTranslation("finance");
  const dispatch = useDispatch();
  const { selectedCompany, companyList } = useSelector((state) => state.companyList);
  const { openSidebar } = useSelector((state) => state.openSidebar);
  const { updateBalanceNeeded } = useSelector((state) => state.flujocaja);
  const holdingCompanyIds = useMemo(() => {
    const ids = openSidebar?.editData?.companyIds;
    return Array.isArray(ids) ? [...new Set(ids.map(String).filter(Boolean))] : [];
  }, [openSidebar?.editData?.companyIds]);
  const isHoldingView = holdingCompanyIds.length > 1;
  const companyId =
    holdingCompanyIds.length === 1
      ? holdingCompanyIds[0]
      : selectedCompany?.id;

  const singleBalances = GetInitialBalances(companyId);
  const holdingBalances = GetBankBalanceDataHolding({
    companyIds: isHoldingView ? holdingCompanyIds : [],
  });
  const manualDocuments = GetManualDocuments(companyId);

  const companyNameById = useMemo(() => {
    const map = new Map();
    (Array.isArray(companyList) ? companyList : []).forEach((company) => {
      const id = String(company?.id || company?._id || "");
      if (!id) return;
      map.set(id, company.alias || company.name || id);
    });
    return map;
  }, [companyList]);

  const bankList = useMemo(() => {
    if (!isHoldingView) {
      return singleBalances?.data?.allBankBalances || [];
    }
    return (holdingBalances?.data?.allBankBalancesHolding || []).map((bank) => ({
      ...bank,
      companyName: companyNameById.get(String(bank.companyId)) || bank.companyId,
    }));
  }, [
    isHoldingView,
    singleBalances?.data?.allBankBalances,
    holdingBalances?.data?.allBankBalancesHolding,
    companyNameById,
  ]);

  const balancesFetching = isHoldingView
    ? holdingBalances.fetching
    : singleBalances.fetching;

  const reexecuteBalances = isHoldingView
    ? holdingBalances.reexecuteQuery
    : singleBalances.reexecuteQuery;

  useEffect(() => {
    if (updateBalanceNeeded?.value) {
      reexecuteBalances({ requestPolicy: "network-only" });
      manualDocuments.reexecuteQuery({ requestPolicy: "network-only" });
      dispatch(importUpdateBalanceNeeded({ value: false }));
      dispatch(importUpdateCashflowNeeded({ value: true, reload: null }));
      if (isHoldingView) {
        dispatch(importUpdateHoldingCashFlowNeeded({ value: true }));
      }
    }
  }, [updateBalanceNeeded?.value, isHoldingView]);

  function addInitialBalance() {
    dispatch(
      importOpenSidebar({
        show: true,
        createBalance: true,
        editData: {},
      })
    );
  }

  return (
    <div className="initialBalanceContainer">
      <div
        className="mt-3 ml-3"
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: "20px",
        }}
      >
        {/* reserved for update button */}
      </div>
      {balancesFetching ? (
        <div className="initialBalanceContainer__loadingContainer">
          <div className="initialBalanceContainer__loadingContainer__loading"></div>
        </div>
      ) : bankList.length > 0 ? (
        <BankCard
          initialBalances={{ data: { allBankBalances: bankList } }}
          toMoney={formatMoneyWithCurrency}
          companyId={companyId}
          showCompanyName={isHoldingView}
        />
      ) : (
        <div className="py-4 buttonTopCheck">
          {t("cashflow.noBankInfo")}
        </div>
      )}
      {!isHoldingView && (
        <>
          <h5 className="initialBalanceContainer__subtitle">
            {t("cashflow.addedBalances")}
          </h5>
          <div className="initialBalanceContainer__balancesContainer mt-3">
            {manualDocuments?.fetching ? (
              <div className="initialBalanceContainer__container">
                <div className="initialBalanceContainer__container__preloaderContainer">
                  <div className="initialBalanceContainer__container__preloaderContainer__loading"></div>
                </div>
              </div>
            ) : manualDocuments?.data?.allInitialBalanceDocumentsByCompany?.length >
              0 ? (
              <AddedBalance
                manualDocuments={manualDocuments}
                moment={moment}
                toMoney={formatMoney}
              />
            ) : (
              ""
            )}
            <div className="addedBalanceAdd mb-4">
              <Button
                onClick={(event) => {
                  event.preventDefault();
                  addInitialBalance();
                }}
              >
                <Icon name="plus" />
                {t("cashflow.addBalance")}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
